import React, { useState, useMemo } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Edges, Plane } from "@react-three/drei";
import * as THREE from "three";

export const Solid3D = ({ config }) => {
  const solidType = config.solidType || 'rectangularPrism';
  const showCrossSection = config.showCrossSection || false;
  const showCavalieriDemo = config.showCavalieriDemo || false;

  const [crossSectionHeight, setCrossSectionHeight] = useState(config.height ? config.height / 2 : 2);
  const [shearAmount, setShearAmount] = useState(0); // For Cavalieri

  // Base dimensions
  const dims = {
    l: config.length || 4,
    w: config.width || 4,
    h: config.height || 4,
    r: config.radius || 2
  };

  const renderGeometry = () => {
    switch(solidType) {
      case 'cylinder':
        return <cylinderGeometry args={[dims.r, dims.r, dims.h, 32]} />;
      case 'cone':
        return <coneGeometry args={[dims.r, dims.h, 32]} />;
      case 'sphere':
        return <sphereGeometry args={[dims.r, 32, 32]} />;
      case 'pyramid':
        return <coneGeometry args={[dims.r, dims.h, 4]} />; // 4 segments = square pyramid
      case 'rectangularPrism':
      default:
        return <boxGeometry args={[dims.l, dims.h, dims.w]} />;
    }
  };

  // Cavalieri's Principle representation (stack of slices)
  const renderCavalieriSlices = () => {
    const slices = config.sliceCount || 15;
    const sliceHeight = dims.h / slices;

    return Array.from({ length: slices }).map((_, i) => {
      const yOffset = (i * sliceHeight) - (dims.h / 2) + (sliceHeight / 2);
      const xOffset = shearAmount * (yOffset / dims.h); // Shear transformation

      return (
          <mesh key={i} position={[xOffset, yOffset, 0]}>
            {solidType === 'cylinder' && <cylinderGeometry args={[dims.r, dims.r, sliceHeight * 0.9, 32]} />}
            {solidType === 'rectangularPrism' && <boxGeometry args={[dims.l, sliceHeight * 0.9, dims.w]} />}
            <meshStandardMaterial color="#1890ff" transparent opacity={0.8} />
            <Edges color="black" />
          </mesh>
      );
    });
  };

  return (
      <div className="ch-solid3d-widget">
        <div style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
          {config.showCrossSection && (
              <div style={{ flex: 1 }}>
                <label>Cross-Section Height: {crossSectionHeight.toFixed(1)}</label>
                <input
                    type="range"
                    min={-dims.h/2} max={dims.h/2} step="0.1"
                    value={crossSectionHeight - (dims.h/2)}
                    onChange={(e) => setCrossSectionHeight(parseFloat(e.target.value) + (dims.h/2))}
                    style={{ width: '100%' }}
                />
              </div>
          )}
          {showCavalieriDemo && (
              <div style={{ flex: 1 }}>
                <label>Shear Factor (Cavalieri's):</label>
                <input
                    type="range" min="-5" max="5" step="0.1"
                    value={shearAmount} onChange={(e) => setShearAmount(parseFloat(e.target.value))}
                    style={{ width: '100%' }}
                />
              </div>
          )}
        </div>

        <div style={{ height: '400px', backgroundColor: '#f5f5f5', borderRadius: '8px', overflow: 'hidden' }}>
          <Canvas camera={{ position: [6, 4, 6], fov: 50 }}>
            <ambientLight intensity={0.6} />
            <directionalLight position={[10, 10, 5]} intensity={1} />
            <OrbitControls enablePan={false} enableZoom={true} />

            {/* Main Solid or Cavalieri Slices */}
            {showCavalieriDemo ? (
                <group>{renderCavalieriSlices()}</group>
            ) : (
                <mesh>
                  {renderGeometry()}
                  <meshStandardMaterial color="#1890ff" transparent opacity={showCrossSection ? 0.3 : 0.9} />
                  <Edges color="#096dd9" />
                </mesh>
            )}

            {/* Cross Section Plane */}
            {showCrossSection && !showCavalieriDemo && (
                <Plane
                    args={[dims.l * 2, dims.w * 2]}
                    position={[0, crossSectionHeight - (dims.h/2), 0]}
                    rotation={[-Math.PI / 2, 0, 0]}
                >
                  <meshBasicMaterial color="#fa541c" transparent opacity={0.6} side={THREE.DoubleSide} />
                </Plane>
            )}

            {/* Coordinate Axes */}
            {config.showAxes !== false && <axesHelper args={[5]} />}
          </Canvas>
        </div>

        {/* Analytics Panel */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '15px' }}>
          <div style={{ padding: '10px', background: '#e6f7ff', borderRadius: '6px' }}>
            <strong>Solid Type:</strong> {solidType}
            <br/>
            <strong>Dimensions:</strong> {dims.h} height
          </div>
          {showCavalieriDemo && (
              <div style={{ padding: '10px', background: '#fff7e6', borderRadius: '6px' }}>
                <strong>Cavalieri's Principle:</strong>
                <p style={{ margin: '5px 0 0 0', fontSize: '13px' }}>
                  Even though the stack is shifted, the cross-sectional area at any height remains constant, therefore the total volume is unchanged.
                </p>
              </div>
          )}
        </div>
      </div>
  );
};